import { IconButton, Paper, Tooltip, Typography, makeStyles, withStyles } from '@material-ui/core';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import React from 'react';
import { useDispatch } from 'react-redux';
import allActions from '../../../actions';
import { unoTheme } from '../../../assets/styles/unoTheme';
import MyTable from '../../utils/MyTable';
import { MyTab, MyTabs } from '../../utils/MyTabs';
import {
    RULE_BASIS,
    RULE_STATUS,
    RULE_STATUS_PRESENTATION,
    buildRulesOverview,
    getRuleSkippedNote,
    getRuleStatus,
    getRuleSummaryNote,
} from '../../utils/complianceUtils';
import { applyMaskAmount } from '../../utils/utils';

const LightTooltip = withStyles((theme) => ({
    arrow: {
        "&:before": {
            border: "1px solid #E6E8ED"
        },
        color: theme.palette.common.white
    },
    tooltip: {
        backgroundColor: theme.palette.common.white,
        border: "1px solid #E6E8ED",
        color: "#4A4A4A",
        fontSize: '14px',
        maxWidth: '420px',
    }
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
        borderBottom: 'solid 1px rgba(0, 0, 0, 0.12)',
        backgroundColor: unoTheme.hoverTable,
    },
    leftHeader: {
        display: 'flex',
        flexDirection: 'column',
    },
    fundLabel: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#1C2228'
    },
    accountLabel: {
        fontSize: '14px',
        color: unoTheme.secondaryColor,
    },

    //Corpo rolável: as regras são empilhadas, cada uma com sua própria tabela.
    //Os 148px descontam o cabeçalho e a faixa de abas
    main: {
        height: 'calc(100vh - 148px)',
        overflowY: 'scroll',
        padding: theme.spacing(3),
        textAlign: 'left',
    },

    ruleBlock: {
        marginBottom: theme.spacing(4),
    },
    ruleTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: 'bold',
        color: unoTheme.outOfComplianceColor,
    },
    ruleName: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1C2228',
        marginTop: '2px',
    },
    ruleDescription: {
        fontSize: '13px',
        color: unoTheme.secondaryColor,
        marginTop: '4px',
    },
    ruleBase: {
        fontSize: '13px',
        color: '#1C2228',
        marginTop: '4px',
    },
    infoIcon: {
        fontSize: '16px',
        color: unoTheme.mainColor,
        cursor: 'pointer',
    },
    tableSpacing: {
        marginTop: theme.spacing(2),
    },
    outValue: {
        color: unoTheme.outOfComplianceColor,
        fontWeight: '600',
    },
    green: {
        color: unoTheme.greenMoney,
    },
    baseValue: {
        color: unoTheme.secondaryColor,
    },
    stressedLabel: {
        fontSize: '11px',
        color: unoTheme.secondaryColor,
    },
    pendingBlock: {
        background: '#FFF8E6',
        border: '1px solid #F2D98C',
        borderRadius: '4px',
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
    },
    pendingTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#1C2228',
    },
    pendingIcon: {
        fontSize: '18px',
        color: unoTheme.warningColorStrong,
    },

    pendingText: {
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        marginTop: '4px',
    },
    bacenLabel: {
        fontSize: '11px',
        color: unoTheme.warningColorStrong,
    },
    pendingItem: {
        fontSize: '12px',
        color: '#1C2228',
        marginTop: '4px',
    },
    pendingName: {
        fontWeight: '600',
    },
    pendingRule: {
        fontWeight: '700',
    },
    pendingAsset: {
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        marginLeft: theme.spacing(1),
    },
    pendingSuggestion: {
        color: unoTheme.secondaryColor,
    },

    tabsCompliance: {
        background: unoTheme.hoverTable,
        borderTop: 'solid 1px rgba(0, 0, 0, 0.08)',
        paddingLeft: theme.spacing(2),
    },

    //O cabeçalho da regra herda a cor do estado. Estas classes vêm depois de ruleTitle no
    //stylesheet de propósito: é a ordem de definição que decide, e não a do className
    successRule: {
        color: unoTheme.greenMoney,
    },
    warningRule: {
        color: unoTheme.warningColorStrong,
    },
    errorRule: {
        color: unoTheme.outOfComplianceColor,
    },
    neutralRule: {
        color: unoTheme.secondaryColor,
    },

    statusChip: {
        fontSize: '11px',
        fontWeight: '700',
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        padding: '2px 8px',
        borderRadius: '10px',
    },
    successChip: {
        color: unoTheme.greenMoney,
        background: 'rgba(8, 194, 27, 0.10)',
    },
    warningChip: {
        color: unoTheme.warningColorStrong,
        background: 'rgba(211, 84, 0, 0.10)',
    },
    errorChip: {
        color: unoTheme.outOfComplianceColor,
        background: 'rgba(211, 47, 47, 0.10)',
    },
    neutralChip: {
        color: unoTheme.secondaryColor,
        background: 'rgba(158, 166, 178, 0.15)',
    },

    summaryLine: {
        fontSize: '13px',
        color: '#1C2228',
        marginTop: '6px',
    },
    skippedLine: {
        fontSize: '12px',
        color: unoTheme.warningColorStrong,
        marginTop: '2px',
    },
    outLine: {
        fontSize: '12px',
        color: unoTheme.outOfComplianceColor,
        marginTop: '2px',
    },
    headroomValue: {
        color: unoTheme.greenMoney,
        fontWeight: '600',
    },
    bacenOk: {
        fontSize: '11px',
        fontWeight: '700',
        color: unoTheme.greenMoney,
    },
    entityName: {
        fontSize: '12px',
        color: '#1C2228',
    },
}));

//O que a célula mostra quando o lado não está vinculado — o único caso em que nada se sabe
const UNKNOWN_BACEN_TEXTS = {
    no_link: 'sem instituição vinculada',
    no_entity: 'não encontrado no cadastro',
};

//A R10 não tem base da carteira: no lugar dela mostram-se os dois termos da razão, para que
//RPPS ÷ Total = Apurado seja conferível na própria linha
function AdministratorRppsCell({ row, classes }) {

    const admin = row.administrator ? row.administrator : {};

    return (
        <>
            <div className={classes.entityName}>
                {admin.anbima_name ? admin.anbima_name : (admin.name ? admin.name : '-')}
            </div>
            {admin.rpps !== null && admin.rpps !== undefined && admin.total ?
                <div className={classes.stressedLabel}>
                    {applyMaskAmount(admin.rpps, true) + ' de ' + applyMaskAmount(admin.total, true)}
                </div>
                : null}
        </>
    );
}

//Substitui o percentual nas regras de elegibilidade: a R9 não mede proporção, diz quem é S1/S2
function EntityBacenCell({ side, classes }) {

    if (!side || !side.name) {
        return <span className={classes.baseValue}>{'-'}</span>;
    }

    //Três estados, não quatro: S1/S2; vinculado e portanto negativo; e sem vínculo, que é a
    //única forma de desconhecimento desde que o vazio passou a ser resposta
    const unknownKey = side.entity_id ? 'no_link' : 'no_entity';

    return (
        <>
            <div className={classes.entityName}>{side.name}</div>
            {side.is_bacen_s1_s2 ?
                <div className={classes.bacenOk}>{side.status_bacen}</div>
                : (side.linked ?
                    <div className={classes.bacenLabel}>{'não é S1/S2'}</div>
                    :
                    <div className={classes.bacenLabel}>{UNKNOWN_BACEN_TEXTS[unknownKey]}</div>)}
        </>
    );
}

//O motor devolve o motivo e os dados; o texto de tela fica aqui. Cada natureza tem a sua
//correção, e é ela que o aviso precisa dizer — mandar cadastrar nome de gestora quando o que
//falta é a instituição da conta só faz o usuário procurar no lugar errado
const PENDING_TEXTS = {
    no_manager_total: {
        title: group => `${group.items.length} ${group.items.length == 1 ? 'gestora não verificada' : 'gestoras não verificadas'}`,
        hint: 'O volume de recursos geridos por essas gestoras não foi encontrado no arquivo da ANBIMA. '
            + 'Cadastre o nome alternativo delas em Gestores e Administradores para que sejam avaliadas.',
    },
    no_manager_data: {
        title: group => `${group.assets_count} ${group.assets_count == 1 ? 'fundo sem gestora' : 'fundos sem gestora'} no cadastro`,
        hint: 'Esses fundos não têm gestora nos dados cadastrais, então não há a quem atribuir o saldo '
            + 'na concentração por gestora.',
    },
    zero_total: {
        title: group => `${group.items.length} ${group.items.length == 1 ? 'gestora sem volume apurado' : 'gestoras sem volume apurado'}`,
        hint: 'Essas gestoras constam no arquivo da ANBIMA, mas sem volume de recursos geridos, '
            + 'então não há denominador para o cálculo.',
    },
    no_snapshot: {
        title: () => 'concentração por gestora não verificada',
        hint: 'Não há arquivo da ANBIMA disponível até a competência desta carteira.',
    },
    no_issuer: {
        title: group => `${group.assets_count} ${group.assets_count == 1 ? 'ativo sem emissor identificado' : 'ativos sem emissor identificado'}`,
        hint: 'A conta desses ativos não tem instituição vinculada, e é a instituição da conta que '
            + 'identifica o emissor. Vincule a instituição no cadastro da conta.',
    },
    no_bacen_link: {
        title: group => `${group.items.length} ${group.items.length == 1 ?
            'gestor ou administrador sem instituição vinculada'
            : 'gestores e administradores sem instituição vinculada'}`,
        hint: 'Sem saber a qual instituição o gestor ou o administrador pertence não há como ler '
            + 'a segmentação S1/S2 do Banco Central. Vincule a instituição em '
            + 'Administração › Instituições, nas abas Gestores e Administradores.',
    },
    no_anbima_admin: {
        title: group => `${group.items.length} ${group.items.length == 1 ?
            'administrador não verificado' : 'administradores não verificados'}`,
        hint: 'O percentual de recursos oriundos de RPPS desses administradores não foi encontrado '
            + 'no arquivo da ANBIMA. Cadastre o nome alternativo deles em '
            + 'Administração › Instituições, aba Administradores, para que sejam avaliados.',
    },
    zero_administrator_total: {
        title: group => `${group.items.length} ${group.items.length == 1 ?
            'administrador sem volume apurado' : 'administradores sem volume apurado'}`,
        hint: 'Esses administradores constam no arquivo da ANBIMA, mas sem total de recursos, '
            + 'então não há denominador para a razão entre RPPS e total.',
    },
    default: {
        title: group => `${group.items.length} ${group.items.length == 1 ? 'pendência' : 'pendências'}`,
        hint: 'Não foi possível apurar a regra para esses itens.',
    },
};

export default function ArticlesRulesCompliance({ content }) {

    const dispatch = useDispatch();
    const classes = useStyles();

    const {
        data: { byCode, resolutionName, totalViolations, notEvaluated, initialTab }
    } = content;

    //Carteira enquadrada abre nas regras contempladas; havendo o que corrigir, abre onde está
    const [tabValue, setTabValue] = React.useState(initialTab ? initialTab : 0);

    //Clicar no botão do header com o painel já aberto atualiza o content sem desmontar o
    //componente, e sem isto a aba pedida seria ignorada
    React.useEffect(() => {
        setTabValue(initialTab ? initialTab : 0);
    }, [initialTab]);

    //O quadro sai do próprio byCode, e não do que o header calculou: uma fonte só, e o painel
    //segue correto mesmo aberto de outro lugar
    const rulesOverview = buildRulesOverview(byCode);

    //Regras sem desenquadramento não são exibidas
    const groupsToShow = Object.values(byCode || {})
        .filter(group => group.violations && group.violations.length);

    //O que impediu cada regra de ser apurada. São naturezas diferentes — gestora sem volume no
    //arquivo, fundo sem gestora, ativo sem emissor — e cada uma tem a sua regra e a sua correção,
    //então viram blocos separados em vez de uma lista só
    const pendingGroups = [];

    (notEvaluated || []).forEach(item => {

        let group = pendingGroups.filter(entry => entry.reason == item.reason)[0];

        if (!group) {
            group = {
                reason: item.reason,
                rule_codes: item.rule_codes ? item.rule_codes : [],
                rule_article: item.rule_article,
                items: [],
                balance: 0,
                assets_count: 0,
            };
            pendingGroups.push(group);
        }

        group.items.push(item);
        group.balance += parseFloat(item.balance) || 0;
        group.assets_count += item.assets ? item.assets.length : 0;

    });

    //A R2 nasce inerte enquanto a segmentação do Bacen não é cadastrada. Dizer apenas
    //"não aplicável" faria entender que a carteira não tem emissor S1/S2, quando o que falta é
    //cadastro — e é sob o limite menor, o da R3, que esses emissores estão sendo avaliados
    function getBacenUnknownNote(group) {

        if (group.rule.code != 'R2' || getRuleStatus(group) != RULE_STATUS.NOT_APPLICABLE) {
            return null;
        }

        const issuerRule = byCode ? byCode['R3'] : null;

        if (!issuerRule) {
            return null;
        }

        const unknown = issuerRule.violations
            .concat(issuerRule.compliances ? issuerRule.compliances : [])
            .filter(item => item.bacen_unknown);

        if (!unknown.length) {
            return null;
        }

        const balance = unknown.reduce((total, item) => total + (parseFloat(item.balance) || 0), 0);

        return `${unknown.length} ${unknown.length == 1 ? 'emissor está' : 'emissores estão'} sem `
            + `classificação Bacen cadastrada (${applyMaskAmount(balance, true)}) e por isso foram `
            + 'avaliados pela R3, de limite menor. A classificação é cadastrada em '
            + 'Administração › Instituições.';
    }

    return (
        <div className={classes.root}>

            <Paper style={{ marginBottom: '4px' }}>
                <div className={classes.header}>
                    <div className={classes.leftHeader}>
                        <Typography
                            align={'left'}
                            className={classes.fundLabel}>
                            {'Enquadramento'}
                        </Typography>
                        <Typography
                            align={'left'}
                            className={classes.accountLabel}>
                            {`Resolução ${resolutionName || '5.272'}`}
                            {totalViolations ?
                                <span style={{ marginLeft: '8px', color: unoTheme.outOfComplianceColor }}>
                                    {totalViolations} {totalViolations == 1 ? 'ocorrência' : 'ocorrências'}
                                </span>
                                :
                                <span style={{ marginLeft: '8px', color: unoTheme.greenMoney }}>
                                    {'sem desenquadramentos'}
                                </span>
                            }
                        </Typography>
                    </div>

                    <IconButton onClick={() => dispatch(allActions.rightContentActions.setRightContent(null))}>
                        <CloseIcon />
                    </IconButton>
                </div>

                {/* As duas leituras da mesma apuração: o que está fora do limite e o que está
                    dentro dele. Antes só a primeira tinha onde aparecer */}
                <Paper square className={classes.tabsCompliance} elevation={0}>
                    <MyTabs
                        value={tabValue}
                        indicatorColor="primary"
                        textColor="primary"
                        onChange={(event, value) => setTabValue(value)}>

                        <MyTab value={0} label={'Desenquadramentos'
                            + (totalViolations ? ' (' + totalViolations + ')' : '')} />

                        <MyTab value={1} label={'Contempladas'
                            + ' (' + rulesOverview.counts[RULE_STATUS.COMPLIANT] + ')'} />

                    </MyTabs>
                </Paper>
            </Paper>

            {tabValue == 1 ?
                <div className={classes.main}>

                    {rulesOverview.groups.map(group => {

                        const { rule, summary } = group;

                        const status = getRuleStatus(group);
                        const presentation = RULE_STATUS_PRESENTATION[status] ?
                            RULE_STATUS_PRESENTATION[status]
                            : RULE_STATUS_PRESENTATION[RULE_STATUS.NOT_EVALUATED];

                        //Mesma lógica de colunas da tabela de desenquadramentos: base única no
                        //cabeçalho quando o denominador é da carteira, coluna quando varia por linha
                        const isPortfolioRule = rule.basis == RULE_BASIS.PORTFOLIO;
                        //A regra de elegibilidade não tem denominador nenhum: no lugar das
                        //colunas de percentual ela mostra o gestor e o administrador
                        const isEligibilityRule = rule.basis == RULE_BASIS.ELIGIBILITY;
                        //A R10 tem percentual, mas o denominador é do arquivo da ANBIMA e não da
                        //carteira: no lugar da coluna de base vão os dois termos da razão
                        const isEntityRatioRule = rule.basis == RULE_BASIS.ENTITY_RPPS_SHARE;
                        const hasBaseColumn = !isPortfolioRule && !isEligibilityRule
                            && !isEntityRatioRule;
                        const baseLabel = rule.basis == RULE_BASIS.FUND_PL ?
                            'PL do fundo' : 'Total gerido';

                        const subjectLabel = rule.subject ? rule.subject : 'Ativo';
                        const isAggregatedRule = subjectLabel != 'Ativo';

                        //Quem está mais perto do limite aparece primeiro: é de lá que vem o
                        //próximo desenquadramento
                        //Sem percentual, o que interessa é o maior saldo já confirmado
                        const rows = (group.compliances ? group.compliances : []).slice()
                            .sort((a, b) => isEligibilityRule ?
                                parseFloat(b.balance) - parseFloat(a.balance)
                                : parseFloat(b.percent) - parseFloat(a.percent));

                        const bacenNote = getBacenUnknownNote(group);

                        return (
                            <div className={classes.ruleBlock} key={'rule-ok-' + rule.code}>

                                <div className={[classes.ruleTitle,
                                    classes[presentation.tone + 'Rule']].join(' ')}>
                                    <span>
                                        {`${rule.code} · Art. ${rule.article}`
                                            + (rule.limit != null ? ` · limite ${rule.limit}%` : '')}
                                    </span>
                                    <span className={[classes.statusChip,
                                        classes[presentation.tone + 'Chip']].join(' ')}>
                                        {presentation.label}
                                    </span>
                                    <LightTooltip
                                        title={rule.criteria}
                                        placement="bottom"
                                        arrow>
                                        <InfoIcon className={classes.infoIcon} />
                                    </LightTooltip>
                                </div>

                                <Typography className={classes.ruleName}>
                                    {rule.name}
                                </Typography>

                                <Typography className={classes.ruleDescription}>
                                    {rule.description}
                                </Typography>

                                <Typography className={classes.summaryLine}>
                                    {getRuleSummaryNote(group)}
                                </Typography>

                                {summary.skipped_count ?
                                    <Typography className={classes.skippedLine}>
                                        {getRuleSkippedNote(group)}
                                    </Typography>
                                    : null}

                                {bacenNote ?
                                    <Typography className={classes.skippedLine}>
                                        {bacenNote}
                                    </Typography>
                                    : null}

                                {status == RULE_STATUS.OUT ?
                                    <Typography className={classes.outLine}>
                                        {`${summary.violated_count} ${summary.violated_count == 1 ?
                                            'desenquadramento' : 'desenquadramentos'} nesta regra — o `
                                            + 'detalhamento está na aba Desenquadramentos.'}
                                    </Typography>
                                    : null}

                                {isPortfolioRule && summary.base ?
                                    <Typography className={classes.ruleBase}>
                                        {'Saldo investido da carteira: '}
                                        <strong>{applyMaskAmount(summary.base, true)}</strong>
                                    </Typography>
                                    : null}

                                {summary.competence ?
                                    <Typography className={classes.ruleBase}>
                                        {(isEntityRatioRule ?
                                            'Recursos de RPPS e total gerido conforme arquivo da ANBIMA de '
                                            : 'Volume gerido conforme arquivo da ANBIMA de ')
                                            + `${summary.competence.month}/${summary.competence.year}`}
                                    </Typography>
                                    : null}

                                {rows.length ?
                                    <div className={classes.tableSpacing}>
                                        <MyTable
                                            size="small"
                                            heightAuto>

                                            {isEligibilityRule ?
                                                <colgroup>
                                                    <col width="30%" />
                                                    <col width="8%" />
                                                    <col width="16%" />
                                                    <col width="23%" />
                                                    <col width="23%" />
                                                </colgroup>
                                                : (isEntityRatioRule ?
                                                <colgroup>
                                                    <col width="26%" />
                                                    <col width="8%" />
                                                    <col width="16%" />
                                                    <col width="26%" />
                                                    <col width="9%" />
                                                    <col width="7%" />
                                                    <col width="8%" />
                                                </colgroup>
                                                : (hasBaseColumn ?
                                                <colgroup>
                                                    <col width="29%" />
                                                    <col width="10%" />
                                                    <col width="17%" />
                                                    <col width="17%" />
                                                    <col width="9%" />
                                                    <col width="8%" />
                                                    <col width="10%" />
                                                </colgroup>
                                                :
                                                <colgroup>
                                                    <col width="38%" />
                                                    <col width="12%" />
                                                    <col width="20%" />
                                                    <col width="10%" />
                                                    <col width="10%" />
                                                    <col width="10%" />
                                                </colgroup>))
                                            }

                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="left">{subjectLabel}</TableCell>
                                                    <TableCell align="center">{'Enquadr.'}</TableCell>
                                                    <TableCell align="right">{'Saldo'}</TableCell>
                                                    {isEligibilityRule ?
                                                        <>
                                                            <TableCell align="left">{'Gestor'}</TableCell>
                                                            <TableCell align="left">{'Administrador'}</TableCell>
                                                        </>
                                                        :
                                                        <>
                                                            {isEntityRatioRule ?
                                                                <TableCell align="left">{'Administrador'}</TableCell>
                                                                : null}
                                                            {hasBaseColumn ?
                                                                <TableCell align="right">{baseLabel}</TableCell>
                                                                : null}
                                                            <TableCell align="right">{'Apurado'}</TableCell>
                                                            <TableCell align="right">{'Limite'}</TableCell>
                                                            <TableCell align="right">{'Folga'}</TableCell>
                                                        </>
                                                    }
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {rows.map((row, index) => (
                                                    <TableRow key={'compliance-' + rule.code + '-' + index}>
                                                        <TableCell align="left">
                                                            {row.subject_name}
                                                            {isAggregatedRule && (row.funds_count || row.assets_count) ?
                                                                <div className={classes.stressedLabel}>
                                                                    {row.funds_count ?
                                                                        `${row.funds_count} ${row.funds_count == 1 ? 'fundo' : 'fundos'}`
                                                                        : `${row.assets_count} ${row.assets_count == 1 ? 'ativo' : 'ativos'}`}
                                                                </div>
                                                                : null}
                                                            {row.bacen_unknown ?
                                                                <div className={classes.bacenLabel}>
                                                                    {'sem classificação Bacen — limite de 2,5% aplicado por falta de cadastro'}
                                                                </div>
                                                                : null}
                                                            {row.stressed ?
                                                                <div className={classes.stressedLabel}>{'fundo estressado'}</div>
                                                                : null}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {row.article_device_abbreviation || '-'}
                                                        </TableCell>
                                                        <TableCell align="right" className={classes.green}>
                                                            {applyMaskAmount(row.balance, true)}
                                                        </TableCell>
                                                        {isEligibilityRule ?
                                                            <>
                                                                <TableCell align="left">
                                                                    <EntityBacenCell side={row.manager} classes={classes} />
                                                                </TableCell>
                                                                <TableCell align="left">
                                                                    {row.same_entity ?
                                                                        <span className={classes.baseValue}>
                                                                            {'o mesmo do gestor'}
                                                                        </span>
                                                                        : <EntityBacenCell side={row.administrator} classes={classes} />}
                                                                </TableCell>
                                                            </>
                                                            :
                                                            <>
                                                                {isEntityRatioRule ?
                                                                    <TableCell align="left">
                                                                        <AdministratorRppsCell row={row} classes={classes} />
                                                                    </TableCell>
                                                                    : null}
                                                                {hasBaseColumn ?
                                                                    <TableCell align="right" className={classes.baseValue}>
                                                                        {applyMaskAmount(row.base, true)}
                                                                    </TableCell>
                                                                    : null}
                                                                <TableCell align="right">
                                                                    {applyMaskAmount(row.percent) + '%'}
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    {rule.limit + '%'}
                                                                </TableCell>
                                                                <TableCell align="right" className={classes.headroomValue}>
                                                                    {applyMaskAmount(row.headroom) + ' p.p.'}
                                                                </TableCell>
                                                            </>
                                                        }
                                                    </TableRow>
                                                ))}
                                            </TableBody>

                                        </MyTable>
                                    </div>
                                    : null}

                            </div>
                        );
                    })}

                </div>
                : null}

            {tabValue == 0 ?
            <div className={classes.main}>

                {pendingGroups.map((group, groupIndex) => {

                    const texts = PENDING_TEXTS[group.reason] ?
                        PENDING_TEXTS[group.reason] : PENDING_TEXTS.default;

                    return (
                        <div className={classes.pendingBlock} key={'pending-group-' + groupIndex}>

                            <div className={classes.pendingTitle}>
                                <WarningIcon className={classes.pendingIcon} />
                                <span>
                                    {group.rule_codes.length ?
                                        <span className={classes.pendingRule}>
                                            {`${group.rule_codes.join(' e ')}${group.rule_article ? ' · Art. ' + group.rule_article : ''} — `}
                                        </span>
                                        : null}
                                    {texts.title(group)}
                                    {' — '}
                                    <strong>{applyMaskAmount(group.balance, true)}</strong>
                                </span>
                            </div>

                            <Typography className={classes.pendingText}>
                                {texts.hint}
                            </Typography>

                            {group.items.map((item, index) => (
                                <div className={classes.pendingItem} key={'pending-item-' + groupIndex + '-' + index}>

                                    {item.subject_name ?
                                        <div>
                                            <span className={classes.pendingName}>{item.subject_name}</span>
                                            {item.instituition_name ?
                                                <span className={classes.pendingSuggestion}>
                                                    {' — vinculado a ' + item.instituition_name}
                                                </span>
                                                : null}
                                            {item.suggestions && item.suggestions.length ?
                                                <span className={classes.pendingSuggestion}>
                                                    {' — no arquivo pode ser: ' + item.suggestions.join(', ')}
                                                </span>
                                                : null}
                                        </div>
                                        : null}

                                    {(item.assets || []).map((asset, assetIndex) => (
                                        <div className={classes.pendingAsset}
                                            key={'pending-asset-' + groupIndex + '-' + index + '-' + assetIndex}>
                                            {(asset.fund_name ? asset.fund_name : 'Ativo sem nome')
                                                + ' — ' + applyMaskAmount(asset.balance, true)
                                                + (asset.number_account ? ' — conta ' + asset.number_account : '')}
                                        </div>
                                    ))}

                                </div>
                            ))}

                        </div>
                    );
                })}

                {groupsToShow.map(group => {

                    //A base da carteira é uma só e fica no cabeçalho da seção. As demais mudam de
                    //linha para linha — o patrimônio de cada fundo, o volume gerido por cada gestora —
                    //e por isso viram coluna, com o rótulo do que está sendo comparado
                    const isPortfolioRule = group.rule.basis == RULE_BASIS.PORTFOLIO;
                    const isEligibilityRule = group.rule.basis == RULE_BASIS.ELIGIBILITY;
                    const isEntityRatioRule = group.rule.basis == RULE_BASIS.ENTITY_RPPS_SHARE;
                    const hasBaseColumn = !isPortfolioRule && !isEligibilityRule
                        && !isEntityRatioRule;
                    const baseLabel = group.rule.basis == RULE_BASIS.FUND_PL ?
                        'PL do fundo' : 'Total gerido';

                    //Regras agregadas põem vários ativos numa linha só — uma gestora, um emissor.
                    //O que a linha representa vem do catálogo, e não de uma cadeia de ifs por código
                    const subjectLabel = group.rule.subject ? group.rule.subject : 'Ativo';
                    const isAggregatedRule = subjectLabel != 'Ativo';

                    return (
                        <div className={classes.ruleBlock} key={'rule-block-' + group.rule.code}>

                            <div className={classes.ruleTitle}>
                                <span>
                                    {`${group.rule.code} · Art. ${group.rule.article}`
                                        + (group.rule.limit != null ?
                                            ` · limite ${group.rule.limit}%` : '')}
                                </span>
                                <LightTooltip
                                    title={group.rule.criteria}
                                    placement="bottom"
                                    arrow>
                                    <InfoIcon className={classes.infoIcon} />
                                </LightTooltip>
                            </div>

                            <Typography className={classes.ruleName}>
                                {group.rule.name}
                            </Typography>

                            <Typography className={classes.ruleDescription}>
                                {group.rule.description}
                            </Typography>

                            {isPortfolioRule ?
                                <Typography className={classes.ruleBase}>
                                    {'Saldo investido da carteira: '}
                                    <strong>{applyMaskAmount(group.violations[0].base, true)}</strong>
                                </Typography>
                                : null}

                            {group.violations[0].manager_competence ?
                                <Typography className={classes.ruleBase}>
                                    {`Volume gerido conforme arquivo da ANBIMA de ${group.violations[0].manager_competence.month}/${group.violations[0].manager_competence.year}`}
                                </Typography>
                                : null}

                            <div className={classes.tableSpacing}>
                                <MyTable
                                    size="small"
                                    heightAuto>

                                    {isEligibilityRule ?
                                        <colgroup>
                                            <col width="30%" />
                                            <col width="8%" />
                                            <col width="16%" />
                                            <col width="23%" />
                                            <col width="23%" />
                                        </colgroup>
                                        : (isEntityRatioRule ?
                                        <colgroup>
                                            <col width="26%" />
                                            <col width="8%" />
                                            <col width="16%" />
                                            <col width="26%" />
                                            <col width="9%" />
                                            <col width="7%" />
                                            <col width="8%" />
                                        </colgroup>
                                        : (hasBaseColumn ?
                                        <colgroup>
                                            <col width="29%" />
                                            <col width="10%" />
                                            <col width="17%" />
                                            <col width="17%" />
                                            <col width="9%" />
                                            <col width="8%" />
                                            <col width="10%" />
                                        </colgroup>
                                        :
                                        <colgroup>
                                            <col width="38%" />
                                            <col width="12%" />
                                            <col width="20%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                            <col width="10%" />
                                        </colgroup>))
                                    }

                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left">{subjectLabel}</TableCell>
                                            <TableCell align="center">{'Enquadr.'}</TableCell>
                                            <TableCell align="right">{'Saldo'}</TableCell>
                                            {isEligibilityRule ?
                                                <>
                                                    <TableCell align="left">{'Gestor'}</TableCell>
                                                    <TableCell align="left">{'Administrador'}</TableCell>
                                                </>
                                                :
                                                <>
                                                    {isEntityRatioRule ?
                                                        <TableCell align="left">{'Administrador'}</TableCell>
                                                        : null}
                                                    {hasBaseColumn ?
                                                        <TableCell align="right">{baseLabel}</TableCell>
                                                        : null}
                                                    <TableCell align="right">{'Apurado'}</TableCell>
                                                    <TableCell align="right">{'Limite'}</TableCell>
                                                    <TableCell align="right">{'Excesso'}</TableCell>
                                                </>
                                            }
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {group.violations.map((violation, index) => (
                                            <TableRow key={'violation-' + group.rule.code + '-' + index}>
                                                <TableCell align="left">
                                                    {violation.fund_name}
                                                    {isAggregatedRule && (violation.funds_count || violation.assets_count) ?
                                                        <div className={classes.stressedLabel}>
                                                            {violation.funds_count ?
                                                                `${violation.funds_count} ${violation.funds_count == 1 ? 'fundo' : 'fundos'}`
                                                                : `${violation.assets_count} ${violation.assets_count == 1 ? 'ativo' : 'ativos'}`}
                                                        </div>
                                                        : null}
                                                    {violation.bacen_unknown ?
                                                        <div className={classes.bacenLabel}>
                                                            {'sem classificação Bacen — limite de 2,5% aplicado por falta de cadastro'}
                                                        </div>
                                                        : null}
                                                    {violation.stressed ?
                                                        <div className={classes.stressedLabel}>{'fundo estressado'}</div>
                                                        : null}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {violation.article_device_abbreviation || '-'}
                                                </TableCell>
                                                <TableCell align="right" className={classes.green}>
                                                    {applyMaskAmount(violation.balance, true)}
                                                </TableCell>
                                                {isEligibilityRule ?
                                                    <>
                                                        <TableCell align="left">
                                                            <EntityBacenCell side={violation.manager} classes={classes} />
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {violation.same_entity ?
                                                                <span className={classes.baseValue}>
                                                                    {'o mesmo do gestor'}
                                                                </span>
                                                                : <EntityBacenCell side={violation.administrator} classes={classes} />}
                                                        </TableCell>
                                                    </>
                                                    :
                                                    <>
                                                        {isEntityRatioRule ?
                                                            <TableCell align="left">
                                                                <AdministratorRppsCell row={violation} classes={classes} />
                                                            </TableCell>
                                                            : null}
                                                        {hasBaseColumn ?
                                                            <TableCell align="right" className={classes.baseValue}>
                                                                {applyMaskAmount(violation.base, true)}
                                                            </TableCell>
                                                            : null}
                                                        <TableCell align="right" className={classes.outValue}>
                                                            {applyMaskAmount(violation.percent) + '%'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {violation.rule_limit + '%'}
                                                        </TableCell>
                                                        <TableCell align="right" className={classes.outValue}>
                                                            {'+' + applyMaskAmount(violation.excess) + ' p.p.'}
                                                        </TableCell>
                                                    </>
                                                }
                                            </TableRow>
                                        ))}
                                    </TableBody>

                                </MyTable>
                            </div>

                        </div>
                    );
                })}

            </div>
                : null}

        </div>
    );
}
